import React, { useContext, useEffect, useState } from "react";
import { FormSelect } from "react-bootstrap";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  PieChart,
  Pie,
  Sector,
} from "recharts";

import ContextComponent from "../../../AppContext";
import Utils from "../../../Utils";
import { DashboardUtility } from "./DashboardUtility";

const CHART_COLORS = [
  "#4682B4",
  "#5F9EA0",
  "#66CDAA",
  "#8FBC8F",
  "#9ACD32",
  "#BDB76B",
  "#ffb400",
  "#dda30b",
  "#ff9500",
  "#FA8072",
  "#CD5C5C",
  "#DC143C",
  "#C71585",
  "#9932CC",
  "#8A2BE2",
];

const BAR_GRAPH_COLORS = {
  TOP_1: "#66CDAA",
  TOP_3: "#5F9EA0",
  DEFAULT: "#4682B4",
};

const getBarColor = (index) => {
  if (index === 0) return BAR_GRAPH_COLORS.TOP_1;
  if (index < 3) return BAR_GRAPH_COLORS.TOP_3;
  return BAR_GRAPH_COLORS.DEFAULT;
};

const addChartColors = (data = []) => {
  return data.map((item, index) => ({
    ...item,
    color: CHART_COLORS[index % CHART_COLORS.length],
  }));
};

const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 10}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};

const CustomBarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="dashboard-chart-tooltip">
      <div className="dashboard-chart-tooltip-title">
        {payload[0].payload.country}
      </div>

      <div className="dashboard-chart-tooltip-value">
        {payload[0].value.toLocaleString()} submissions
      </div>
    </div>
  );
};

const CustomDonutTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="dashboard-chart-tooltip">
      <div className="dashboard-chart-tooltip-title">{payload[0].name}</div>

      <div className="dashboard-chart-tooltip-value">{payload[0].value}</div>
    </div>
  );
};

const getTopDataWithOthers = (
  data = [],
  limit = 10,
  labelKey = "name",
  valueKey = "value",
) => {
  if (data.length <= limit) {
    return data;
  }

  const sortedData = [...data].sort((a, b) => b[valueKey] - a[valueKey]);

  const topItems = sortedData.slice(0, limit);

  const remainingItems = sortedData.slice(limit);

  const othersValue = remainingItems.reduce(
    (sum, item) => sum + item[valueKey],
    0,
  );

  return [
    ...topItems,
    {
      [labelKey]: "Others",
      [valueKey]: othersValue,
      color: "#adb5bd",
    },
  ];
};

const DonutLegend = ({ data }) => {
  return (
    <div className="demographics-legend">
      {data.map((item) => (
        <div className="demographics-legend-item" key={item?.name}>
          <span
            className="demographics-legend-color"
            style={{
              background: item?.color,
            }}
          />

          <span className="demographics-legend-text">{item?.name}</span>

          <span className="demographics-legend-value">{item.value}</span>
        </div>
      ))}
    </div>
  );
};

const Demographics = () => {
  const { setLoadingState, setAlert, setCanRedirectToLogin, userData } =
    useContext(ContextComponent);

  const [superAdmin, setSuperAdmin] = useState(false);
  const [activeIndustryIndex, setActiveIndustryIndex] = useState(0);

  const [activeJobIndex, setActiveJobIndex] = useState(0);
  const [selectedSite, setSelectedSite] = useState("");

  const [siteOptions, setSiteOptions] = useState([]);

  const [demographicData, setDemographicData] = useState({
    countries: [],
    industries: [],
    jobTitles: [],
  });
  const [canRender, setCanRender] = useState(false);



  useEffect(() => {
    const isSuperAdmin = !!userData?.roles?.includes("ROLE_SUPER_ADMIN");
    setSuperAdmin(isSuperAdmin);
    loadSites(isSuperAdmin);
  }, []);

  const ALL_ASSESSMENTS = {
    id: "all-assessments",
    name: "All assessments",
    value: "all",
  };

  const loadSites = (isSuperAdmin) => {
    const userId = userData?.id;
    DashboardUtility?.loadSitesForReport({
      userId,
      setLoadingState,
      setAlert,
      setCanRedirectToLogin,
      onSuccess: (sortedSites) => {
        const mappedSites = sortedSites.map((site) => ({
          ...site,
          value: site?.name,
        }));

        // Commenting out the "All assessments" option for now, as per the requirement
        // const siteList = isSuperAdmin
        //   ? [ALL_ASSESSMENTS, ...mappedSites]
        //   : mappedSites;
        const siteList = [ALL_ASSESSMENTS, ...mappedSites];

        setSiteOptions(siteList);

        const defaultSite = isSuperAdmin
          ? ALL_ASSESSMENTS?.value
          : mappedSites[0]?.value;

        setSelectedSite(defaultSite);
        loadDemographicsData(defaultSite);
      },
    });
  };

  const loadDemographicsData = (siteName) => {
    const fetchDemographicsDataSuccess = (res) => {
      setLoadingState({
        applyMask: false,
      });

      const responseData = res?.data;

      const formattedCountries = getTopDataWithOthers(
        responseData?.countries?.map((item) => ({
          country: item?.name,
          submissions: item?.count,
        })) || [],
        10,
        "country",
        "submissions",
      );

      const formattedIndustries = addChartColors(
        getTopDataWithOthers(
          responseData?.industries?.map((item) => ({
            name: item?.name,
            value: item?.count,
          })) || [],
          10,
        ),
      );

      const formattedJobTitles = addChartColors(
        getTopDataWithOthers(
          responseData?.jobTitles?.map((item) => ({
            name: item?.name,
            value: item?.count,
          })) || [],
          10,
        ),
      );

      setDemographicData({
        countries: formattedCountries,
        industries: formattedIndustries,
        jobTitles: formattedJobTitles,
      });

      setCanRender(
        formattedCountries.length > 0 ||
        formattedIndustries.length > 0 ||
        formattedJobTitles.length > 0,
      );
    };

    const fetchDemographicsDataFailure = (err) => {
      setLoadingState({
        applyMask: false,
      });
      setAlert({
        show: true,
        message: "Failed to load demographics data",
        type: "danger"
      });
      console.error("Error fetching demographics data:", err);
      setCanRender(false);
      if (err?.redirect) {
        setCanRedirectToLogin(true);
      }
    };

    setLoadingState({
      applyMask: true,
      text: "Loading demographics data",
    });

    Utils.getDemographicsData(siteName).then(
      fetchDemographicsDataSuccess,
      fetchDemographicsDataFailure,
    );
  };

  const handleChangeSiteData = (e) => {
    const siteName = e?.target?.value;

    setSelectedSite(siteName);

    loadDemographicsData(siteName);
  };

  return (
    <div className="demographics-container">
      <div className="demographics-header">
        <div className="demographics-header-content">
          <h5 className="demographics-title">
            Demographic breakdown from form data
          </h5>

          <p className="demographics-subtitle">
            Country, industry and job title split from anonymous user
            registrations
          </p>
        </div>

        <div className="demographics-select-wrapper">
          <FormSelect
            className="demographics-select"
            value={selectedSite}
            onChange={handleChangeSiteData}
            title={selectedSite}
          >
            {siteOptions.map((site) => (
              <option
                key={site?.id}
                value={site?.value}
                className="demographics-option"
              >
                {site?.name}
              </option>
            ))}
          </FormSelect>
        </div>
      </div>

      {canRender ? (
        <>
          <div className="demographics-card">
            <div className="demographics-card-title">
              Top countries by submission
            </div>

            <div className="demographics-country-chart">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={demographicData?.countries}
                  layout="vertical"
                  margin={{
                    top: 0,
                    right: 30,
                    left: 20,
                    bottom: 20,
                  }}
                >
                  <XAxis
                    type="number"
                    tick={{
                      fontSize: 11,
                      fill: "#304697",
                    }}
                    tickLine={false}
                    axisLine={false}
                  />

                  <YAxis
                    type="category"
                    dataKey="country"
                    width={120}
                    tick={{
                      fontSize: 12,
                      fill: "#304697",
                    }}
                    tickLine={false}
                    axisLine={false}
                  />

                  <Tooltip
                    content={<CustomBarTooltip />}
                    cursor={false}
                    shared={false}
                  />

                  <Bar
                    dataKey="submissions"
                    radius={[0, 4, 4, 0]}
                    barSize={22}
                    cursor="pointer"
                    animationDuration={1000}
                  >
                    {demographicData?.countries.map((item, index) => (
                      <Cell key={item.country} fill={getBarColor(index)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="demographics-bottom-grid">
            <div className="demographics-card">
              <div className="demographics-card-title">
                Business industry split
              </div>

              <div className="demographics-donut-wrapper">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      activeIndex={activeIndustryIndex}
                      activeShape={renderActiveShape}
                      onMouseEnter={(_, index) => {
                        setActiveIndustryIndex(index);
                      }}
                      data={demographicData?.industries}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={70}
                      outerRadius={95}
                      paddingAngle={2}
                    >
                      {demographicData?.industries.map((entry) => (
                        <Cell key={entry?.name} fill={entry?.color} />
                      ))}
                    </Pie>

                    <Tooltip content={<CustomDonutTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <DonutLegend data={demographicData?.industries} />
            </div>

            <div className="demographics-card">
              <div className="demographics-card-title">Job title split</div>

              <div className="demographics-donut-wrapper">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      activeIndex={activeJobIndex}
                      activeShape={renderActiveShape}
                      onMouseEnter={(_, index) => {
                        setActiveJobIndex(index);
                      }}
                      data={demographicData?.jobTitles}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={70}
                      outerRadius={95}
                      paddingAngle={2}
                    >
                      {demographicData?.jobTitles.map((entry) => (
                        <Cell key={entry?.name} fill={entry?.color} />
                      ))}
                    </Pie>

                    <Tooltip content={<CustomDonutTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <DonutLegend data={demographicData?.jobTitles} />
            </div>
          </div>
        </>
      ) : (
        <div className="dashboard-no-data-found">No data found</div>
      )}
    </div>
  );
};

export default Demographics;
